import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logAuditAction } from '@/hooks/useAuditLog';

// ---------- Types ----------

export interface ForumAuthor {
  id: string;
  full_name: string;
  avatar_url: string | null;
  company: string | null;
}

export interface ForumThreadRow {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  tags: string[] | null;
  status: string | null;
  external_link: string | null;
  related_project_id: string | null;
  related_workshop_id: string | null;
  created_at: string;
  last_activity_at: string | null;
  author_id: string;
  author: ForumAuthor;
  response_count: number;
}

export interface ForumResponseRow {
  id: string;
  thread_id: string;
  content: string;
  is_solution: boolean | null;
  created_at: string;
  author_id: string;
  author: ForumAuthor;
}

// ---------- Hooks ----------

export function useForumThreads() {
  return useQuery({
    queryKey: ['forum-threads'],
    queryFn: async (): Promise<ForumThreadRow[]> => {
      // Fetch threads with author profile
      const { data: threads, error } = await supabase
        .from('forum_threads')
        .select('*, profiles!forum_threads_author_id_fkey(id, full_name, avatar_url, company)')
        .order('last_activity_at', { ascending: false, nullsFirst: false });

      if (error) throw error;

      // Fetch response counts per thread
      const { data: responseCounts, error: rcError } = await supabase
        .from('forum_responses')
        .select('thread_id');

      if (rcError) throw rcError;

      const countMap: Record<string, number> = {};
      responseCounts?.forEach((r) => {
        countMap[r.thread_id] = (countMap[r.thread_id] || 0) + 1;
      });

      return (threads || []).map((t: any) => ({
        id: t.id,
        title: t.title,
        description: t.description,
        category: t.category,
        tags: t.tags,
        status: t.status,
        external_link: t.external_link,
        related_project_id: t.related_project_id,
        related_workshop_id: t.related_workshop_id,
        created_at: t.created_at,
        last_activity_at: t.last_activity_at,
        author_id: t.author_id,
        author: t.profiles as ForumAuthor,
        response_count: countMap[t.id] || 0,
      }));
    },
  });
}

export function useForumThread(threadId: string | undefined) {
  return useQuery({
    queryKey: ['forum-thread', threadId],
    enabled: !!threadId,
    queryFn: async (): Promise<ForumThreadRow | null> => {
      const { data, error } = await supabase
        .from('forum_threads')
        .select('*, profiles!forum_threads_author_id_fkey(id, full_name, avatar_url, company)')
        .eq('id', threadId!)
        .single();

      if (error) throw error;

      // Count responses
      const { count } = await supabase
        .from('forum_responses')
        .select('*', { count: 'exact', head: true })
        .eq('thread_id', threadId!);

      return {
        id: data.id,
        title: data.title,
        description: data.description,
        category: data.category,
        tags: data.tags,
        status: data.status,
        external_link: data.external_link,
        related_project_id: data.related_project_id,
        related_workshop_id: data.related_workshop_id,
        created_at: data.created_at,
        last_activity_at: data.last_activity_at,
        author_id: data.author_id,
        author: (data as any).profiles as ForumAuthor,
        response_count: count || 0,
      };
    },
  });
}

export function useForumResponses(threadId: string | undefined) {
  return useQuery({
    queryKey: ['forum-responses', threadId],
    enabled: !!threadId,
    queryFn: async (): Promise<ForumResponseRow[]> => {
      const { data, error } = await supabase
        .from('forum_responses')
        .select('*, profiles!forum_responses_author_id_fkey(id, full_name, avatar_url, company)')
        .eq('thread_id', threadId!)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return (data || []).map((r: any) => ({
        id: r.id,
        thread_id: r.thread_id,
        content: r.content,
        is_solution: r.is_solution,
        created_at: r.created_at,
        author_id: r.author_id,
        author: r.profiles as ForumAuthor,
      }));
    },
  });
}

export function useCreateThread() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: {
      title: string;
      description: string;
      category: string;
      tags?: string[];
      external_link?: string;
    }) => {
      const { data, error } = await supabase
        .from('forum_threads')
        .insert({
          author_id: user!.id,
          title: input.title,
          description: input.description,
          category: input.category,
          tags: input.tags || [],
          external_link: input.external_link || null,
          last_activity_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['forum-threads'] });
      if (user) {
        logAuditAction(user.id, 'forum_post', `Nuevo tema en foro: ${data.title}`, { thread_id: data.id, category: data.category } as any);
      }
    },
  });
}

export function useCreateResponse() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: { threadId: string; content: string }) => {
      const { data, error } = await supabase
        .from('forum_responses')
        .insert({
          thread_id: input.threadId,
          author_id: user!.id,
          content: input.content,
        })
        .select()
        .single();

      if (error) throw error;
      // last_activity_at is bumped automatically by DB trigger
      return data;
    },
    onSuccess: (data, variables) => {
      qc.invalidateQueries({ queryKey: ['forum-threads'] });
      qc.invalidateQueries({ queryKey: ['forum-thread', variables.threadId] });
      qc.invalidateQueries({ queryKey: ['forum-responses', variables.threadId] });
      if (user) {
        logAuditAction(user.id, 'forum_reply', 'Respuesta en foro', { thread_id: variables.threadId, response_id: data.id } as any);
      }
    },
  });
}

export function useMarkSolution() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: { responseId: string; threadId: string; mark: boolean }) => {
      // First unmark any existing solution
      if (input.mark) {
        await supabase
          .from('forum_responses')
          .update({ is_solution: false })
          .eq('thread_id', input.threadId)
          .eq('is_solution', true);
      }

      const { error } = await supabase
        .from('forum_responses')
        .update({ is_solution: input.mark })
        .eq('id', input.responseId);

      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['forum-responses', variables.threadId] });
    },
  });
}
